import { useEffect, useRef, useState } from "react";
import "./CallForProposalsListPage.scss";
import {
  GridRow,
  GridPresetWideList,
  TabList,
  LoadingOverlay,
  InfoSmallIcon,
  CalendarBlankIcon,
  Pagination,
  HeroBasic,
  ScreenReaderOnlyText,
  ListItemGroupCallForProposals,
} from "/App.Components";
import { CallForProposalsListPageProps } from "/Models/Generated/CallForProposalsListPageProps";
import CallForProposalsListClient from "./CallForProposalsListClient";
import { NewsListSectionProps } from "/Models/Generated/NewsListSectionProps";
import useScrollTopTracker from "/Helpers/Hooks/useScrollTopTracker";
import createBem from "/Helpers/createBem";

const CallForProposalsPage = ({
  pageHeader,
  endpoint,
  pageId,
  language,
  result,
  pageNumber,
  selectedTab,
  activeCallForProposalsLabel,
  activeCallForProposalsLabelPropertyName,
  expiredCallForProposalsLabel,
  expiredCallForProposalsLabelPropertyName,
  allocationsLabel,
  allocationsLabelPropertyName,
  mobileDropdownLabel,
  pagination,
}: CallForProposalsListPageProps) => {
  const [currentPageNumber, setCurrentPageNumber] = useState(pageNumber);
  const [currentSelectedTab, setCurrentSelectedTab] = useState(
    (selectedTab as "active" | "expired" | "allocations") ?? "active",
  );
  const [currentListingIcon, setCurrentListingIcon] = useState(
    (selectedTab as "active" | "expired" | "allocations") ?? "active",
  );
  const [currentResult, setCurrentResult] = useState(result);
  const [loading, setLoading] = useState(false);
  const [doScroll, setDoScroll] = useState(false);
  const [tabChangeAriaText, setTabChangeAriaText] = useState("");
  const scrollToRef = useRef<HTMLDivElement>(null);
  const scrollTop = useScrollTopTracker(scrollToRef);

  useEffect(() => {
    if (loading) return;
    if (!doScroll) return;
    setDoScroll(false);
    window.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  }, [loading]);

  useEffect(() => {
    if (!loading) return;
    const client = new CallForProposalsListClient(endpoint, language, pageId);

    client.GetItems(currentPageNumber, currentSelectedTab).then((result) => {
      setCurrentResult(result);
      const newUrl = client.BuildUrlFromProps(
        currentPageNumber,
        currentSelectedTab,
      );
      window.history.replaceState({}, "", newUrl);
      setCurrentListingIcon(currentSelectedTab);
      setLoading(false);
    });
  }, [loading]);

  const changeTab = (tab: "active" | "expired" | "allocations") => {
    if (tab === currentSelectedTab) return;

    const tabLabel =
      tab === "active"
        ? activeCallForProposalsLabel
        : tab === "expired"
          ? expiredCallForProposalsLabel
          : allocationsLabel;

    setTabChangeAriaText(tabLabel ? `Viser ${tabLabel}` : "Viser oppdatert innhold");
    setCurrentPageNumber(1);
    setCurrentSelectedTab(tab);
    setLoading(true);
  };

  const bem = createBem("call-for-proposals-list-page-norad");
  return (
    <div className={bem("")}>
      {pageHeader && <HeroBasic {...pageHeader} />}
      <LoadingOverlay loading={loading} />
      <GridRow>
        <GridPresetWideList>
          <ScreenReaderOnlyText ariaLive="polite" text={tabChangeAriaText} />
          <div className={bem("__tab-list-container")} ref={scrollToRef}>
            <TabList
              mobileHeading={mobileDropdownLabel}
              tabButtons={[
                {
                  text: activeCallForProposalsLabel,
                  propertyName: activeCallForProposalsLabelPropertyName,
                  onClick: () => changeTab("active"),
                  selected: currentSelectedTab === "active",
                },
                {
                  text: expiredCallForProposalsLabel,
                  propertyName: expiredCallForProposalsLabelPropertyName,
                  onClick: () => changeTab("expired"),
                  selected: currentSelectedTab === "expired",
                },
                {
                  text: allocationsLabel,
                  propertyName: allocationsLabelPropertyName,
                  onClick: () => changeTab("allocations"),
                  selected: currentSelectedTab === "allocations",
                },
              ]}
            />
          </div>
          {(currentResult?.totalItems ?? 0) > 0 && (
            <ol className={bem("__list")}>
              {(currentResult?.items as NewsListSectionProps[]).map(
                (item, idx) => (
                  <ListItemGroupCallForProposals
                    showLabel
                    key={"item-group" + idx}
                    {...item}
                    iconType={currentListingIcon}
                    overrideGetMetaDataItems={(item) =>
                      item.deadline
                        ? {
                            firstRow: (
                              <InfoSmallIcon
                                icon={CalendarBlankIcon}
                                text={item.deadline}
                              />
                            ),
                          }
                        : item.published
                          ? {
                              firstRow: <InfoSmallIcon text={item.published} />,
                            }
                          : undefined
                    }
                  />
                ),
              )}
            </ol>
          )}
          {currentResult && (currentResult?.totalPages ?? 1) > 1 && (
            <Pagination
              {...pagination}
              currentPage={currentPageNumber}
              setCurrentPage={(pageNumber: number) => {
                setCurrentPageNumber(pageNumber);
                setDoScroll(true);
                setLoading(true);
              }}
              totalPages={currentResult.totalPages}
            />
          )}
        </GridPresetWideList>
      </GridRow>
    </div>
  );
};

export default CallForProposalsPage;
